import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Minus, Trash2, Pencil, AlertTriangle, PackageMinus } from "lucide-react";

const emptySupplier = { name: "", contact: "", phone: "", email: "", notes: "" };
const emptySupply = { name: "", unit: "box", quantity: 0, unit_cost: 0, low_threshold: 0, category: "Materials", notes: "" };
const units = ["box", "bag", "gallon", "bottle", "each", "roll", "case"];
const cats = ["Materials", "Chemicals", "Fuel", "Equipment", "Office", "Other"];

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptySupplier);
  const [editing, setEditing] = useState(null);
  // supplies dialogs
  const [supOpen, setSupOpen] = useState(false);
  const [supForm, setSupForm] = useState(emptySupply);
  const [supEditing, setSupEditing] = useState(null);
  const [useTarget, setUseTarget] = useState(null);
  const [useQty, setUseQty] = useState(1);
  const [useNote, setUseNote] = useState("");

  const load = () => {
    api.get("/catalog").then((r) => setItems(r.data.filter((i) => i.track_inventory)));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/supplies").then((r) => setSupplies(r.data));
  };
  useEffect(() => { load(); }, []);

  const adjust = async (id, delta) => { await api.post(`/catalog/${id}/adjust-stock?delta=${delta}`); load(); };

  const saveSupplier = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const body = { ...form }; delete body.id; delete body.created_at;
    try {
      if (editing) await api.put(`/suppliers/${editing}`, body);
      else await api.post("/suppliers", body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeSupplier = async (id) => { await api.delete(`/suppliers/${id}`); toast.success("Deleted"); load(); };

  const saveSupply = async () => {
    if (!supForm.name.trim()) return toast.error("Name required");
    const body = { ...supForm, quantity: Number(supForm.quantity) || 0, unit_cost: Number(supForm.unit_cost) || 0, low_threshold: Number(supForm.low_threshold) || 0 };
    delete body.id; delete body.created_at;
    try {
      if (supEditing) await api.put(`/supplies/${supEditing}`, body);
      else await api.post("/supplies", body);
      toast.success("Saved"); setSupOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeSupply = async (id) => { await api.delete(`/supplies/${id}`); toast.success("Deleted"); load(); };

  const logUse = async () => {
    const qty = Number(useQty) || 1;
    try {
      const { data } = await api.post(`/supplies/${useTarget.id}/use`, { quantity: qty, notes: useNote });
      toast.success(`Logged ${qty} ${useTarget.unit} used · ${money(data.expense_amount)} expense · ${data.quantity} left`);
      setUseTarget(null); setUseQty(1); setUseNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const lowStock = items.filter((i) => i.stock_qty <= i.reorder_level);
  const lowSupplies = supplies.filter((s) => s.low_threshold > 0 && s.quantity <= s.low_threshold);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <Tabs defaultValue="supplies">
        <TabsList>
          <TabsTrigger value="supplies" data-testid="tab-supplies">Supplies</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">Catalog Stock</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Suppliers</TabsTrigger>
        </TabsList>

        {/* SUPPLIES */}
        <TabsContent value="supplies" className="space-y-4 mt-5">
          {lowSupplies.length > 0 && (
            <Card className="p-4 border-secondary/40 bg-secondary/5 flex items-center gap-3" data-testid="low-supplies-alert">
              <AlertTriangle className="h-5 w-5 text-secondary" />
              <span className="text-sm"><strong>{lowSupplies.length}</strong> supply(ies) running low: {lowSupplies.map((s) => s.name).join(", ")}</span>
            </Card>
          )}
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Track consumables. Hit "Use" when one is consumed — stock drops and a materials expense is logged automatically.</p>
            <Button data-testid="add-supply-btn" onClick={() => { setSupForm(emptySupply); setSupEditing(null); setSupOpen(true); }}><Plus className="h-4 w-4 mr-1.5" /> Add Supply</Button>
          </div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-muted/50"><TableHead>Item</TableHead><TableHead>Category</TableHead><TableHead className="text-center">In Stock</TableHead><TableHead className="text-right">Unit Cost</TableHead><TableHead className="text-center">Use</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
              <TableBody>
                {supplies.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No supplies yet. Add your first consumable (e.g. black trash bags).</TableCell></TableRow>
                ) : supplies.map((s) => {
                  const low = s.low_threshold > 0 && s.quantity <= s.low_threshold;
                  return (
                    <TableRow key={s.id} data-testid="supply-row" className="hover:bg-muted/30">
                      <TableCell className="font-semibold">{s.name}{s.notes && <div className="text-xs text-muted-foreground font-normal">{s.notes}</div>}</TableCell>
                      <TableCell><Badge variant="outline">{s.category}</Badge></TableCell>
                      <TableCell className="text-center"><Badge className={`tabular-nums border-0 ${low ? "bg-secondary/15 text-secondary" : "bg-primary/10 text-primary"}`}>{s.quantity} {s.unit}{s.quantity === 1 ? "" : "s"}</Badge></TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{money(s.unit_cost)}</TableCell>
                      <TableCell className="text-center">
                        <Button size="sm" variant="outline" data-testid="use-supply-btn" onClick={() => { setUseTarget(s); setUseQty(1); setUseNote(""); }}><PackageMinus className="h-3.5 w-3.5 mr-1" />Use</Button>
                      </TableCell>
                      <TableCell><div className="flex items-center justify-end gap-1">
                        <button data-testid="edit-supply-btn" onClick={() => { setSupForm({ ...emptySupply, ...s }); setSupEditing(s.id); setSupOpen(true); }} className="p-1.5 rounded hover:bg-muted"><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></button>
                        <button data-testid="delete-supply-btn" onClick={() => removeSupply(s.id)} className="p-1.5 rounded hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                      </div></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* CATALOG STOCK */}
        <TabsContent value="stock" className="space-y-4 mt-5">
          {lowStock.length > 0 && (
            <Card className="p-4 border-secondary/40 bg-secondary/5 rounded-lg flex items-center gap-3" data-testid="low-stock-alert">
              <AlertTriangle className="h-5 w-5 text-secondary" />
              <span className="text-sm"><strong>{lowStock.length}</strong> item(s) at or below reorder level: {lowStock.map((i) => i.name).join(", ")}</span>
            </Card>
          )}
          <p className="text-sm text-muted-foreground">Products marked "track inventory" in your catalog appear here.</p>
          <Card className="border-border rounded-lg overflow-hidden bg-card">
            <Table>
              <TableHeader><TableRow className="bg-muted/50"><TableHead>Product</TableHead><TableHead>SKU</TableHead><TableHead className="text-center">In Stock</TableHead><TableHead className="text-right">Reorder At</TableHead><TableHead className="text-center">Adjust</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No tracked products. Enable "Track inventory" on a catalog product.</TableCell></TableRow>
                ) : items.map((it) => {
                  const low = it.stock_qty <= it.reorder_level;
                  return (
                    <TableRow key={it.id} data-testid="inventory-row" className="hover:bg-muted/30 transition-colors duration-200">
                      <TableCell className="font-semibold">{it.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{it.sku || "—"}</TableCell>
                      <TableCell className="text-center"><Badge className={`tabular-nums border-0 ${low ? "bg-secondary/15 text-secondary" : "bg-primary/10 text-primary"}`}>{it.stock_qty} {it.unit}</Badge></TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{it.reorder_level}</TableCell>
                      <TableCell><div className="flex items-center justify-center gap-1">
                        <button data-testid="stock-minus" onClick={() => adjust(it.id, -1)} className="p-1.5 rounded-md hover:bg-muted transition-colors duration-200"><Minus className="h-4 w-4" /></button>
                        <button data-testid="stock-plus" onClick={() => adjust(it.id, 1)} className="p-1.5 rounded-md hover:bg-muted transition-colors duration-200"><Plus className="h-4 w-4" /></button>
                      </div></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* SUPPLIERS */}
        <TabsContent value="suppliers" className="space-y-4 mt-5">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Manage vendors and suppliers.</p>
            <Button data-testid="add-supplier-btn" onClick={() => { setForm(emptySupplier); setEditing(null); setOpen(true); }} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200"><Plus className="h-4 w-4 mr-1.5" /> Add Supplier</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suppliers.length === 0 && <Card className="p-10 text-center border-dashed col-span-full text-muted-foreground">No suppliers yet.</Card>}
            {suppliers.map((s) => (
              <Card key={s.id} data-testid="supplier-card" className="p-5 border-border rounded-lg bg-card">
                <div className="flex justify-between items-start">
                  <div><h3 className="font-display font-bold tracking-tight">{s.name}</h3>{s.contact && <p className="text-sm text-muted-foreground">{s.contact}</p>}</div>
                  <div className="flex gap-1">
                    <button onClick={() => { setForm({ ...emptySupplier, ...s }); setEditing(s.id); setOpen(true); }} className="p-1.5 rounded hover:bg-muted transition-colors duration-200"><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></button>
                    <button onClick={() => removeSupplier(s.id)} className="p-1.5 rounded hover:bg-destructive/10 transition-colors duration-200"><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                  </div>
                </div>
                <div className="mt-3 space-y-1 text-sm text-muted-foreground">{s.phone && <div>{s.phone}</div>}{s.email && <div>{s.email}</div>}</div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Supplier dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Supplier" : "New Supplier"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Name *</Label><Input data-testid="supplier-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Contact Person</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-supplier-btn" onClick={saveSupplier} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supply add/edit dialog */}
      <Dialog open={supOpen} onOpenChange={setSupOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{supEditing ? "Edit Supply" : "New Supply"}</DialogTitle><DialogDescription>A consumable you keep in stock.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5"><Label>Item name *</Label><Input data-testid="supply-name-input" value={supForm.name} onChange={(e) => setSupForm({ ...supForm, name: e.target.value })} placeholder="Black trash bags" /></div>
            <div className="space-y-1.5"><Label>Quantity in stock</Label><Input type="number" data-testid="supply-qty-input" value={supForm.quantity} onChange={(e) => setSupForm({ ...supForm, quantity: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Unit</Label>
              <Select value={supForm.unit} onValueChange={(v) => setSupForm({ ...supForm, unit: v })}><SelectTrigger data-testid="supply-unit-select"><SelectValue /></SelectTrigger><SelectContent>{units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Cost per unit ($)</Label><Input type="number" step="0.01" value={supForm.unit_cost} onChange={(e) => setSupForm({ ...supForm, unit_cost: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Low-stock alert at</Label><Input type="number" value={supForm.low_threshold} onChange={(e) => setSupForm({ ...supForm, low_threshold: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Category</Label>
              <Select value={supForm.category} onValueChange={(v) => setSupForm({ ...supForm, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={supForm.notes} onChange={(e) => setSupForm({ ...supForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setSupOpen(false)}>Cancel</Button><Button data-testid="save-supply-btn" onClick={saveSupply}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use dialog */}
      <Dialog open={!!useTarget} onOpenChange={(o) => !o && setUseTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Log usage</DialogTitle><DialogDescription>{useTarget ? `${useTarget.name} — ${useTarget.quantity} ${useTarget.unit}(s) in stock` : ""}</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>How many {useTarget?.unit}(s) used?</Label><Input type="number" data-testid="use-qty-input" value={useQty} onChange={(e) => setUseQty(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Note (optional)</Label><Input value={useNote} onChange={(e) => setUseNote(e.target.value)} placeholder="e.g. finished a box on the Smith job" /></div>
            {useTarget && <p className="text-xs text-muted-foreground">Logs a {useTarget.category} expense of {money((Number(useQty) || 1) * (useTarget.unit_cost || 0))} and reduces stock to {Math.max(0, (useTarget.quantity || 0) - (Number(useQty) || 1))}.</p>}
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setUseTarget(null)}>Cancel</Button><Button onClick={logUse} data-testid="confirm-use-btn">Log Usage</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
